/**
 * Procedural audio. No files, no fetches, no decoding — every sound in this game
 * is three or four WebAudio nodes wired up on the spot.
 *
 * Two shapes of sound, kept apart because they have completely different costs:
 *
 * - **One-shots** (`bounce`, `sting`) build a tiny throwaway graph per event.
 *   Landings happen once or twice a second, so ~4 nodes each is nothing, and
 *   scheduled nodes free themselves when they stop.
 * - **Continuous** (`chargeTick`, `wind`) reuse a single oscillator and a single
 *   looping noise source created once at {@link init}, and are steered every
 *   frame with `setTargetAtTime` on their gain and filter. Creating a node per
 *   frame for these would be a garbage fire at 144 Hz.
 *
 * Hard rule: **audio never breaks the game.** Browsers refuse to build an
 * AudioContext outside a user gesture, refuse to resume one, run out of node
 * budget, or simply do not implement something. Every entry point is a no-op
 * before `init()` and every one of them swallows its own failures — a silent
 * game is a disappointment, an exception in the render loop is a bug report.
 */

import { settings } from '../core/settings';

/** Overall output level. Everything below is mixed relative to this. */
const MASTER_GAIN = 0.5;

/** Bounce: noise burst length and the band it is filtered to. */
const BURST_MS = 40;
const BURST_HZ = 1500;
const BURST_Q = 1.1;

/** Bounce: the "boing" is `BOING_BASE + speed · BOING_PER_SPEED` hertz. */
const BOING_BASE = 180;
const BOING_PER_SPEED = 22;
const BOING_MS = 220;
/** Impact speed that counts as "as loud as it gets". */
const LOUD_SPEED = 16;

/** Charge sweep: sawtooth from 80 Hz at rest to 300 Hz at full. */
const CHARGE_LO_HZ = 80;
const CHARGE_HI_HZ = 300;
const CHARGE_GAIN = 0.05;

/** Fall wind: silent until this speed, full by that one. */
const WIND_START_VY = -10;
const WIND_FULL_VY = -45;
const WIND_GAIN = 0.16;
const WIND_LO_HZ = 240;
const WIND_HI_HZ = 1400;

/** Big-fall sting: a low thud, not a crash. */
const STING_HZ = 62;
const STING_MS = 700;

/** Smoothing time constants for the continuous voices, seconds. */
const TAU_CHARGE = 0.02;
const TAU_WIND = 0.12;

/** Length of the shared white-noise buffer, seconds. */
const NOISE_SECONDS = 2;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
/** Shared white noise, reused by every burst and by the wind loop. */
let noise: AudioBuffer | null = null;

/** Continuous voices, built once by `init`. */
let chargeOsc: OscillatorNode | null = null;
let chargeGain: GainNode | null = null;
let windGain: GainNode | null = null;
let windFilter: BiquadFilterNode | null = null;

let muted = false;
/** Set once `init` has run to completion; every method is a no-op before that. */
let ready = false;
/** Set when the browser refused us an AudioContext, so we stop trying. */
let broken = false;

function now(): number {
  return ctx === null ? 0 : ctx.currentTime;
}

/** Master level for the current mute state. */
function targetMaster(): number {
  return muted ? 0 : MASTER_GAIN;
}

function makeNoiseBuffer(c: AudioContext): AudioBuffer {
  const frames = Math.floor(c.sampleRate * NOISE_SECONDS);
  const buf = c.createBuffer(1, frames, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

// ---------------------------------------------------------------------------
// public surface
// ---------------------------------------------------------------------------

export interface Audio {
  /**
   * Build the context and the continuous voices. **Must be called from a user
   * gesture** (the CLIMB click) — every browser blocks audio otherwise. Safe to
   * call more than once; later calls only nudge a suspended context awake.
   */
  init(): void;
  /** Mute or unmute. Takes effect immediately, and survives `init` being late. */
  setMuted(m: boolean): void;
  /** True once a context exists and the voices are running. */
  readonly ready: boolean;

  /**
   * A landing.
   *
   * @param speed      launch speed in m/s — sets both pitch and level.
   * @param isMushroom mushroom caps sound an octave down and bend *up*, so the
   *                   1.9× bounce is audible before you see how high you went.
   */
  bounce(speed: number, isMushroom?: boolean): void;
  /** The low thud under a big-fall toast. */
  sting(): void;
  /**
   * Per-frame charge sweep.
   *
   * @param charge   `[0, 1]`.
   * @param charging whether the button is still held — releasing kills the tone
   *                 immediately even though the charge itself decays over 0.3 s.
   */
  chargeTick(charge: number, charging: boolean): void;
  /** Per-frame fall wind, driven by vertical velocity (negative is down). */
  wind(verticalVel: number): void;
  /** Release the context. Nothing calls this in the game; it exists for tests. */
  dispose(): void;
}

export const audio: Audio = {
  get ready(): boolean {
    return ready;
  },

  init(): void {
    if (broken) return;
    muted = settings.mute;

    if (ctx !== null) {
      // Already built: a second CLIMB (or a resumed tab) only needs a nudge.
      void ctx.resume().catch(() => undefined);
      if (master !== null) master.gain.setTargetAtTime(targetMaster(), now(), TAU_CHARGE);
      return;
    }

    try {
      const Ctor: typeof AudioContext | undefined =
        globalThis.AudioContext ??
        (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor === undefined) {
        broken = true;
        return;
      }
      const c = new Ctor();
      ctx = c;
      void c.resume().catch(() => undefined);

      const m = c.createGain();
      m.gain.value = targetMaster();
      m.connect(c.destination);
      master = m;

      noise = makeNoiseBuffer(c);

      // --- charge sweep: one sawtooth, silent until asked for ---------------
      const osc = c.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = CHARGE_LO_HZ;
      const og = c.createGain();
      og.gain.value = 0;
      // A gentle lowpass: a raw saw at 300 Hz is all upper harmonics and hurts.
      const olp = c.createBiquadFilter();
      olp.type = 'lowpass';
      olp.frequency.value = 1200;
      osc.connect(olp).connect(og).connect(m);
      osc.start();
      chargeOsc = osc;
      chargeGain = og;

      // --- fall wind: one looping noise source through a lowpass ------------
      const src = c.createBufferSource();
      src.buffer = noise;
      src.loop = true;
      const wf = c.createBiquadFilter();
      wf.type = 'lowpass';
      wf.frequency.value = WIND_LO_HZ;
      wf.Q.value = 0.7;
      const wg = c.createGain();
      wg.gain.value = 0;
      src.connect(wf).connect(wg).connect(m);
      src.start();
      windFilter = wf;
      windGain = wg;

      ready = true;
    } catch (err) {
      // No audio is a perfectly playable game. Say so once and move on.
      console.warn('[audio] unavailable', err);
      broken = true;
      ctx = null;
      master = null;
      ready = false;
    }
  },

  setMuted(m: boolean): void {
    muted = m;
    if (master === null) return;
    try {
      master.gain.setTargetAtTime(targetMaster(), now(), TAU_CHARGE);
    } catch {
      /* a detached context; nothing to do */
    }
  },

  bounce(speed: number, isMushroom = false): void {
    const c = ctx;
    const m = master;
    if (!ready || c === null || m === null || muted) return;
    try {
      const t = c.currentTime;
      const s = Number.isFinite(speed) ? Math.abs(speed) : 0;
      // Level rides the impulse, but never all the way to zero: even a limp
      // bounce has to be audible or the game feels like it dropped a frame.
      const level = 0.25 + 0.75 * clamp(s / LOUD_SPEED, 0, 1);

      // --- click: a short band-passed noise burst ---------------------------
      if (noise !== null) {
        const src = c.createBufferSource();
        src.buffer = noise;
        // Start somewhere random in the buffer so repeated bounces do not
        // replay the identical 40 ms of noise and turn into a tone.
        const offset = Math.random() * (NOISE_SECONDS - BURST_MS / 1000);
        const bp = c.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = BURST_HZ * (isMushroom ? 0.55 : 1);
        bp.Q.value = BURST_Q;
        const g = c.createGain();
        const burst = BURST_MS / 1000;
        g.gain.setValueAtTime(0.5 * level, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + burst);
        src.connect(bp).connect(g).connect(m);
        src.start(t, offset, burst);
        src.stop(t + burst);
      }

      // --- boing: a sine whose pitch tracks the launch speed -----------------
      const osc = c.createOscillator();
      osc.type = 'sine';
      const base = (BOING_BASE + s * BOING_PER_SPEED) * (isMushroom ? 0.5 : 1);
      const dur = (BOING_MS / 1000) * (isMushroom ? 1.6 : 1);
      osc.frequency.setValueAtTime(base, t);
      if (isMushroom) {
        // Mushrooms bend up — a rising pitch reads as "you are being launched".
        osc.frequency.exponentialRampToValueAtTime(base * 2.2, t + dur * 0.8);
      } else {
        // Everything else sags, the way a real spring unloads.
        osc.frequency.exponentialRampToValueAtTime(Math.max(40, base * 0.6), t + dur);
      }
      const og = c.createGain();
      og.gain.setValueAtTime(0.0001, t);
      og.gain.exponentialRampToValueAtTime(0.55 * level, t + 0.008);
      og.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(og).connect(m);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    } catch {
      /* one lost bounce sound is not worth a frame */
    }
  },

  sting(): void {
    const c = ctx;
    const m = master;
    if (!ready || c === null || m === null || muted) return;
    try {
      const t = c.currentTime;
      const dur = STING_MS / 1000;

      const osc = c.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(STING_HZ * 1.8, t);
      osc.frequency.exponentialRampToValueAtTime(STING_HZ * 0.5, t + dur);
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.9, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(m);
      osc.start(t);
      osc.stop(t + dur + 0.02);

      // A short low-passed noise thump under it, for the body of the impact.
      if (noise !== null) {
        const src = c.createBufferSource();
        src.buffer = noise;
        const lp = c.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 200;
        const ng = c.createGain();
        ng.gain.setValueAtTime(0.6, t);
        ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
        src.connect(lp).connect(ng).connect(m);
        src.start(t, Math.random() * (NOISE_SECONDS - 0.3), 0.25);
        src.stop(t + 0.25);
      }
    } catch {
      /* ignore */
    }
  },

  chargeTick(charge: number, charging: boolean): void {
    const g = chargeGain;
    const osc = chargeOsc;
    if (!ready || g === null || osc === null) return;
    try {
      const t = now();
      const c = Number.isFinite(charge) ? clamp(charge, 0, 1) : 0;
      const want = charging ? CHARGE_GAIN : 0;
      g.gain.setTargetAtTime(want, t, TAU_CHARGE);
      // Keep sweeping even at zero gain: re-pitching a silent oscillator is free
      // and means the next press starts from the right note.
      osc.frequency.setTargetAtTime(
        CHARGE_LO_HZ + (CHARGE_HI_HZ - CHARGE_LO_HZ) * c,
        t,
        TAU_CHARGE,
      );
    } catch {
      /* ignore */
    }
  },

  wind(verticalVel: number): void {
    const g = windGain;
    const f = windFilter;
    if (!ready || g === null || f === null) return;
    try {
      const t = now();
      const vy = Number.isFinite(verticalVel) ? verticalVel : 0;
      // 0 above WIND_START_VY, 1 at terminal velocity.
      const u = clamp((vy - WIND_START_VY) / (WIND_FULL_VY - WIND_START_VY), 0, 1);
      g.gain.setTargetAtTime(WIND_GAIN * u * u, t, TAU_WIND);
      f.frequency.setTargetAtTime(WIND_LO_HZ + (WIND_HI_HZ - WIND_LO_HZ) * u, t, TAU_WIND);
    } catch {
      /* ignore */
    }
  },

  dispose(): void {
    const c = ctx;
    ready = false;
    ctx = null;
    master = null;
    noise = null;
    chargeOsc = null;
    chargeGain = null;
    windGain = null;
    windFilter = null;
    if (c === null) return;
    void c.close().catch(() => undefined);
  },
};
